/**
 * Fase 1 Cobrar (sep-2026) — Venta tipo Booksy sobre una cita.
 * Paso 1 "Venta": artículos, descuento, propina 0/25/30/custom, total en vivo.
 * Paso 2 "Método de pago": QR Stripe (real) y Efectivo (real);
 * tarjeta guardada / Tap to Pay / terminal física como "Próximamente".
 */
import { useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Banknote, CheckCircle2, ChevronLeft, Copy, CreditCard, Mail, Nfc, Plus,
  QrCode, Smartphone, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { friendlyError } from '@/lib/apiError';
import { bookingApi, qrPayApi } from '@/lib/bookingApi';
import { shopsApi } from '@/lib/shopsApi';
import { OutsideHoursSurcharge } from '@/components/Bookings/OutsideHoursSurcharge';
import { PayLinkStatus } from '@/components/Bookings/PayLinkStatus';
import { tr } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';
import { TipPicker, DEFAULT_TIP, tipAmount } from '@/components/Bookings/TipPicker';

const money = (v) => fmtMoney(v);

export const SaleSheet = ({ booking, open, onOpenChange, onPaid }) => {
  const [step, setStep] = useState('venta'); // venta | metodo | qr | done
  const [items, setItems] = useState([]);
  const [itemName, setItemName] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [tipSel, setTipSel] = useState(DEFAULT_TIP); // mismo selector que el pago desde la app del cliente
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [doneMethod, setDoneMethod] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [payLink, setPayLink] = useState(null); // link de pago por correo (Cobrar)
  const [askUpdate, setAskUpdate] = useState(null); // {email} ⇒ ¿actualizar la ficha?
  // BS2 — barbero afiliado a una barbería: propina opcional y separada para el local.
  const [shop, setShop] = useState(null);
  const [shopTip, setShopTip] = useState('');
  // D5 · C1 — recargo manual por horario fuera de servicio ({amount, mode, value} | null).
  const [surcharge, setSurcharge] = useState(null);

  const bookingId = booking?.booking_id || booking?.id;
  const basePrice = Number(booking?.price || 0);
  const outsideHours = Boolean(booking?.outside_hours) && !booking?.premium;

  useEffect(() => {
    if (open) {
      setStep('venta'); setItems([]); setItemName(''); setItemPrice('');
      setDiscount(''); setTipSel(DEFAULT_TIP); setQr(null);
      setDoneMethod(null); setBusy(false);
      setShopTip(''); setShop(null); setSurcharge(null);
      setClientEmail(booking?.client_email || '');
      shopsApi.mine().then((d) => setShop(d?.active || null)).catch(() => setShop(null));
    }
  }, [open, bookingId]);

  const totals = useMemo(() => {
    const sc = Number(surcharge?.amount || 0);
    const itemsTotal = items.reduce((s, i) => s + Number(i.price || 0), 0) + sc;
    const disc = Math.min(Number(discount || 0), basePrice + itemsTotal);
    const subtotal = Math.max(basePrice + itemsTotal - disc, 0);
    const tip = tipAmount(tipSel, subtotal); // % sobre servicio − descuentos
    const sTip = shop ? Math.max(Number(shopTip || 0), 0) : 0;
    return {
      itemsTotal, discount: disc, subtotal, surcharge: sc,
      tip: Math.round(tip * 100) / 100,
      shopTip: Math.round(sTip * 100) / 100,
      total: Math.round((subtotal + tip + sTip) * 100) / 100,
    };
  }, [items, discount, tipSel, basePrice, shop, shopTip, surcharge]);

  // Poll del QR: cuando el cliente paga, cerramos el ciclo.
  useEffect(() => {
    if (step !== 'qr' || !qr?.token) return undefined;
    const iv = setInterval(async () => {
      try {
        const st = await qrPayApi.status(qr.token);
        if (st?.state === 'already_paid') {
          setStep('done'); setDoneMethod('qr');
          toast.success(tr("Pago recibido"));
          onPaid?.();
        }
      } catch { /* poll best-effort */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [step, qr, onPaid]);

  const addItem = () => {
    const p = Number(itemPrice);
    if (!itemName.trim() || !(p >= 0)) return;
    setItems((prev) => [...prev, { name: itemName.trim(), price: Math.round(p * 100) / 100 }]);
    setItemName(''); setItemPrice('');
  };

  const salePayload = (method) => ({
    items: [
      ...items,
      ...(surcharge?.amount > 0
        ? [{ name: tr("Fuera de horario ({0})", surcharge.mode === 'pct' ? `+${surcharge.value}%` : 'monto fijo'), price: surcharge.amount, kind: 'premium_surcharge' }]
        : []),
    ],
    discount: Number(discount || 0),
    ...(tipSel.mode === 'custom' || tipSel.mode === 0
      ? { tip_amount: tipAmount(tipSel, totals.subtotal) }
      : { tip_percent: tipSel.mode }),
    method,
    ...(shop && totals.shopTip > 0 ? { shop_tip_amount: totals.shopTip } : {}),
    ...(clientEmail.trim() ? { client_email: clientEmail.trim() } : {}),
  });

  const chargeQr = async () => {
    setBusy(true);
    try {
      const r = await bookingApi.createSale(bookingId, salePayload('qr'));
      setQr(r.qr); setStep('qr');
    } catch (e) {
      toast.error(e.message || tr("No pudimos generar el cobro"));
    } finally { setBusy(false); }
  };

  const chargeCash = async () => {
    setBusy(true);
    try {
      await bookingApi.createSale(bookingId, salePayload('cash'));
      setStep('done'); setDoneMethod('cash');
      toast.success(tr("Cita marcada como pagada en efectivo"));
      onPaid?.();
    } catch (e) {
      toast.error(e.message || tr("No pudimos registrar el pago"));
    } finally { setBusy(false); }
  };

  // Link de pago por correo: el cliente paga desde «Ver y pagar mi cita» (con cuenta) o con el enlace de 1 uso (sin cuenta).
  const chargeEmail = async (updateRecord = false) => {
    const email = clientEmail.trim();
    if (!email) { toast.error(tr("Escribe el correo del cliente para enviarle el link de pago")); return; }
    const original = (booking?.client_email || '').trim().toLowerCase();
    if (original && original !== email.toLowerCase() && askUpdate === null) { setAskUpdate({ email }); return; }
    setAskUpdate(null); setBusy(true);
    try {
      const r = await bookingApi.createSale(bookingId, { ...salePayload('email'), client_email: email, update_record: updateRecord, tip_percent: 0, tip_amount: 0 });
      setPayLink(r.pay_link); setStep('done'); setDoneMethod('email');
      toast.success(tr("Link de pago enviado a {0}", r.pay_link.email_masked));
      onPaid?.();
    } catch (e) {
      if (e?.status === 409 && e?.payload?.detail?.code === 'name_duplicate') { toast.error(tr("Hay otro cliente con ese nombre: resuélvelo desde la ficha de la cita antes de enviar el link")); }
      else toast.error(friendlyError(e, tr("No se pudo enviar el link de pago")));
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${qr.path}`);
      toast.success(tr("Enlace copiado"));
    } catch { toast.error(tr("No pudimos copiar el enlace")); }
  };

  if (!booking) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" aria-describedby={undefined} className="bg-card border-border rounded-t-2xl max-h-[90vh] overflow-y-auto" data-testid="sale-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2">
            {step !== 'venta' && step !== 'done' && (
              <button onClick={() => setStep(step === 'qr' ? 'metodo' : 'venta')} data-testid="sale-back-btn" aria-label={tr("Volver")}>
                <ChevronLeft className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
            {{ venta: tr("Venta"), metodo: tr("Método de pago"), qr: tr("Cobro con QR"), done: doneMethod === 'email' ? tr("Link de pago enviado") : tr("Cobro completado") }[step]}
          </SheetTitle>
        </SheetHeader>

        {step === 'venta' && (
          <div className="mt-3 space-y-3" data-testid="sale-step-venta">
            <div className="bg-background border border-border rounded-xl p-3 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-muted-foreground text-xs">{tr("Cliente")}</span><span className="font-semibold" data-testid="sale-client-name">{booking.client_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-xs">{tr("Servicio")}</span><span>{booking.service_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-xs">{tr("Precio")}</span><span className="text-primary font-semibold tabular-nums">{money(basePrice)}</span></div>
            </div>

            {/* D5 · C1 — cita fuera de la jornada: recargo manual opcional */}
            {outsideHours && (
              <OutsideHoursSurcharge basePrice={basePrice} value={surcharge} onChange={setSurcharge} />
            )}

            {/* Artículos */}
            <div className="bg-background border border-border rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase">{tr("Artículos extra")}</p>
              {items.map((it, idx) => (
                <div key={`${it.name}-${idx}`} className="flex items-center justify-between text-sm" data-testid={`sale-item-row-${idx}`}>
                  <span className="truncate">{it.name}</span>
                  <span className="flex items-center gap-2 tabular-nums">
                    {money(it.price)}
                    <button onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} data-testid={`sale-item-remove-${idx}`} aria-label={tr("Quitar artículo")}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </button>
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input value={itemName} onChange={(e) => setItemName(e.target.value)} placeholder={tr("Ej. Pomada")} maxLength={60} className="h-9 rounded-lg bg-card border-border text-sm" data-testid="sale-add-item-name" />
                <Input value={itemPrice} onChange={(e) => setItemPrice(e.target.value)} placeholder="$" inputMode="decimal" className="h-9 rounded-lg bg-card border-border text-sm w-24" data-testid="sale-add-item-price" />
                <Button size="sm" variant="outline" onClick={addItem} className="h-9 rounded-lg shrink-0" data-testid="sale-add-item-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Descuento */}
            <div className="bg-background border border-border rounded-xl p-3 flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase">{tr("Descuento ($)")}</p>
              <Input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" inputMode="decimal" className="h-9 rounded-lg bg-card border-border text-sm w-28 text-right" data-testid="sale-discount-input" />
            </div>

            {/* Propina — selector único (TipPicker), igual que en la app del cliente */}
            <div className="bg-background border border-border rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase">{tr("Propina")}</p>
              <TipPicker value={tipSel} onChange={setTipSel} base={totals.subtotal} testPrefix="sale-tip" label={null} />
            </div>

            {/* BS2 — Propina a la barbería (solo barbero afiliado) */}
            {shop && (
              <div className="bg-background border border-border rounded-xl p-3 space-y-2" data-testid="sale-shop-tip-block">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">{tr("Propina a la barbería")}</p>
                  <span className="text-[10px] text-muted-foreground truncate max-w-[50%]">{shop.name || `@${shop.username}`}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input value={shopTip} onChange={(e) => setShopTip(e.target.value)} placeholder={tr("$ opcional")} inputMode="decimal"
                    className="h-9 rounded-lg bg-card border-border text-sm w-32" data-testid="sale-shop-tip-input" />
                  <p className="text-[10px] text-muted-foreground leading-snug flex-1">{tr("Opcional y separada de la propina del barbero; el cliente decide el monto. Va íntegra al local: solo descuenta su parte de Stripe, Ziyup no cobra nada.")}</p>
                </div>
              </div>
            )}

            {/* Resumen */}
            <div className="bg-background border border-primary/30 rounded-xl p-3 text-sm space-y-1" data-testid="sale-summary">
              <div className="flex justify-between text-muted-foreground text-xs"><span>{tr("Subtotal")}</span><span className="tabular-nums">{money(totals.subtotal)}</span></div>
              {totals.surcharge > 0 && <div className="flex justify-between text-amber-300 text-xs" data-testid="sale-surcharge-line"><span>{tr("Incluye recargo fuera de horario")}</span><span className="tabular-nums">+{money(totals.surcharge)}</span></div>}
              <div className="flex justify-between text-muted-foreground text-xs"><span>{tr("Propina")}</span><span className="tabular-nums" data-testid="sale-tip-amount">{money(totals.tip)}</span></div>
              {totals.shopTip > 0 && <div className="flex justify-between text-muted-foreground text-xs" data-testid="sale-shop-tip-line"><span>{tr("Propina a la barbería")}</span><span className="tabular-nums" data-testid="sale-shop-tip-amount">{money(totals.shopTip)}</span></div>}
              {totals.discount > 0 && <div className="flex justify-between text-muted-foreground text-xs"><span>{tr("Descuento")}</span><span className="tabular-nums">-{money(totals.discount)}</span></div>}
              <div className="flex justify-between items-center pt-1 border-t border-border">
                <span className="font-semibold">{tr("Total")}</span>
                <span className="font-display text-2xl font-bold text-primary tabular-nums" data-testid="sale-total">{money(totals.total)}</span>
              </div>
              {Number(booking.amount_paid) > 0 && (
                <p className="text-[11px] text-muted-foreground">{tr("Ya pagado: {0} — se cobrará el saldo restante.", money(booking.amount_paid))}</p>
              )}
            </div>

            <Button onClick={() => setStep('metodo')} className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="sale-continue-btn">{tr("Continuar al cobro · {0}", money(totals.total))}</Button>
          </div>
        )}

        {step === 'metodo' && (
          <div className="mt-3 space-y-2" data-testid="sale-step-metodo">
            <div className="bg-background border border-border rounded-xl p-3 space-y-1.5">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase">{tr("Correo del cliente (recibo + invitación)")}</p>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder={tr("opcional — ej. nombre@correo.com")}
                className="w-full h-9 bg-card border border-border rounded-lg px-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/60"
                data-testid="sale-client-email-input"
              />
            </div>
            <button onClick={chargeQr} disabled={busy} className="w-full flex items-center gap-3 bg-background border border-primary/40 rounded-xl p-3.5 hover:bg-primary/5 transition-colors text-left" data-testid="sale-method-qr">
              <QrCode className="w-5 h-5 text-primary shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-foreground">{tr("QR con Stripe")}</span>
                <span className="block text-xs text-muted-foreground">{tr("El cliente escanea y paga con tarjeta desde su teléfono")}</span>
              </span>
              <span className="font-display font-bold text-primary tabular-nums">{money(totals.total)}</span>
            </button>
            <button onClick={chargeCash} disabled={busy} className="w-full flex items-center gap-3 bg-background border border-emerald-500/40 rounded-xl p-3.5 hover:bg-emerald-500/5 transition-colors text-left" data-testid="sale-method-cash">
              <Banknote className="w-5 h-5 text-emerald-400 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-foreground">{tr("Efectivo")}</span>
                <span className="block text-xs text-muted-foreground">{tr("Marcar como pagada — sin procesar con Stripe")}</span>
              </span>
              <span className="font-display font-bold text-emerald-400 tabular-nums">{money(totals.total)}</span>
            </button>
            <button onClick={() => chargeEmail(false)} disabled={busy} className="w-full flex items-center gap-3 bg-background border border-sky-500/40 rounded-xl p-3.5 hover:bg-sky-500/5 transition-colors text-left" data-testid="sale-method-email">
              <Mail className="w-5 h-5 text-sky-400 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-foreground">{tr("Enviar link de pago por correo")}</span>
                <span className="block text-xs text-muted-foreground">{tr("El cliente elige propina y paga con tarjeta desde el correo «Ver y pagar mi cita»")}</span>
              </span>
              <span className="font-display font-bold text-sky-400 tabular-nums">{money(totals.subtotal)}</span>
            </button>
            {askUpdate && (
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-3 space-y-2" data-testid="sale-email-update-ask">
                <p className="text-xs text-foreground">{tr("El correo cambió respecto a la ficha del cliente. ¿Actualizo también la ficha con {0}?", askUpdate.email)}</p>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full" onClick={() => chargeEmail(true)} data-testid="sale-email-update-yes">{tr("Sí, actualizar ficha")}</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => chargeEmail(false)} data-testid="sale-email-update-no">{tr("No, solo este envío")}</Button>
                </div>
              </div>
            )}
            {booking?.pay_link && <PayLinkStatus booking={booking} onChanged={onPaid} />}
            {[
              { icon: CreditCard, label: tr("Tarjeta guardada"), desc: tr("Cobro directo a la tarjeta del cliente"), tid: 'sale-method-saved-card' },
              { icon: Smartphone, label: 'Tap to Pay (iPhone)', desc: 'Requiere app nativa iOS', tid: 'sale-method-tap-to-pay' },
              { icon: Nfc, label: tr("Terminal física"), desc: 'Lector Stripe WisePOS', tid: 'sale-method-terminal' },
            ].map(({ icon: Icon, label, desc, tid }) => (
              <div key={tid} className="w-full flex items-center gap-3 bg-background/50 border border-border rounded-xl p-3.5 opacity-55" data-testid={tid}>
                <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-muted-foreground">{label}</span>
                  <span className="block text-xs text-muted-foreground">{desc}</span>
                </span>
                <Badge variant="outline" className="border-border text-muted-foreground text-[9px] uppercase tracking-wider shrink-0">{tr("Próximamente")}</Badge>
              </div>
            ))}
          </div>
        )}

        {step === 'qr' && qr && (
          <div className="mt-3 space-y-3 text-center" data-testid="sale-step-qr">
            <p className="font-display text-3xl font-bold text-primary tabular-nums" data-testid="sale-qr-amount">{money(qr.amount_due)}</p>
            <div className="bg-white rounded-2xl p-4 mx-auto w-fit" data-testid="sale-qr-code">
              <QRCode value={`${window.location.origin}${qr.path}`} size={200} />
            </div>
            <p className="text-xs text-muted-foreground">{tr("El cliente escanea el código y paga desde su teléfono. Válido por {0} minutos.", qr.expiry_minutes)}</p>
            <Button variant="outline" onClick={copyLink} className="w-full h-9 rounded-full border-border text-xs gap-1.5" data-testid="sale-qr-link-copy">
              <Copy className="w-3.5 h-3.5" />{' '}{tr("Copiar enlace de pago")}</Button>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 text-center" data-testid="sale-done">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
            <p className="font-display text-xl font-bold text-foreground">
              {doneMethod === 'cash' ? tr("Pagada en efectivo") : doneMethod === 'email' ? tr("Link de pago enviado") : tr("Pago recibido")}
            </p>
            {doneMethod === 'email' && payLink ? (
              <>
                <p className="text-xs text-muted-foreground mt-1" data-testid="sale-email-sent">{tr("Enviado a {0} · pendiente de pago · {1}", payLink.email_masked, money(payLink.amount))}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{tr("Si cobras en efectivo o por QR, el link se anula solo. Si cambias el monto, se envía uno nuevo y el anterior deja de funcionar.")}</p>
              </>
            ) : <p className="text-xs text-muted-foreground mt-1">{tr("Total cobrado: {0}", money(totals.total))}</p>}
            <Button onClick={() => onOpenChange(false)} className="mt-4 h-10 rounded-full bg-primary text-primary-foreground px-8" data-testid="sale-done-close">{tr("Listo")}</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
